import React from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import ConfirmationModal from '@/common/components/modals/general/ConfirmationModal';
import { DeleteIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import { ActivityData, CourseData, SubunitData, SubunitId, UnitId } from 'course-types';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';

function DeleteContentModal() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const activityId: string = useAppSelector(() => store.getState().activity.id);
  const activeSubunitId: SubunitId | undefined = useAppSelector(() => store.getState().subunit.id);
  const activeUnitId: UnitId | undefined = useAppSelector(() => store.getState().unit.id);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  // Helper functions for cleaner data access
  const getUnitData = () => activeCourseData.units[activeUnitId as UnitId];
  const getSubunitData = () => getUnitData()?.subunits[activeSubunitId as SubunitId];

  const getActivitiesFromContext = (): ActivityData[] => {
    return activeSubunitId
      ? (getSubunitData()?.activities ?? [])
      : (getUnitData()?.activities ?? []);
  };

  const currentActivity = getActivitiesFromContext().find((activity) => activity.id === activityId);

  // Common API deletion logic
  const deleteActivityFromAPI = async (activityToRemove: ActivityData) => {
    const payload = {
      activityId: activityToRemove.id,
      type: activityToRemove.type,
      ...(activeSubunitId ? { subunitId: activeSubunitId } : { unitId: activeUnitId }),
    };

    const response = await (await Request.delete('content', payload))?.json();

    if (!response || response.error || !response.status) {
      const context = activeSubunitId ? 'subunit' : 'unit';
      throw new Error(`Failed to delete content from ${context}`);
    }
  };

  // Utility to reindex activities
  const reindexActivities = (activities: ActivityData[]): ActivityData[] => {
    return activities.map((activity, index) => ({ ...activity, index }));
  };

  const handleDeleteContent = async () => {
    try {
      if (activeSubunitId) {
        await deleteSubunitActivity();
      } else {
        await deleteUnitActivity();
      }
      toast.success('Content deleted successfully');
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete content');
      throw error; // Re-throw to let ConfirmationModal handle the error state
    }
  };

  const deleteSubunitActivity = async () => {
    const activities = [...getActivitiesFromContext()];
    const activityToRemove = activities.find(
      (activity) => activity.id === activityId,
    ) as ActivityData;

    await deleteActivityFromAPI(activityToRemove);

    // update course structure if activity blocks downstream content to update prerequisites
    if (activityToRemove.blocks_downstream_content) {
      const courseData = await UserCourseStorageService.setCourseData(activeCourseData.id);
      dispatch(setActiveCourseData(courseData));
      return;
    }

    // Remove activity and reindex
    const filteredActivities = activities.filter((activity) => activity.id !== activityId);
    const reindexedActivities = reindexActivities(filteredActivities);

    const unitData = getUnitData();
    const subunitData = getSubunitData();

    const newCourseData: CourseData = {
      ...activeCourseData,
      units: {
        ...activeCourseData.units,
        [activeUnitId as UnitId]: {
          ...unitData,
          subunits: {
            ...unitData.subunits,
            [activeSubunitId as SubunitId]: {
              ...subunitData,
              activities: reindexedActivities,
            },
          },
        },
      },
    };

    dispatch(setActiveCourseData(newCourseData));
  };

  const deleteUnitActivity = async () => {
    const activities = [...getActivitiesFromContext()];
    const activityToRemove = activities.find(
      (activity) => activity.id === activityId,
    ) as ActivityData;

    await deleteActivityFromAPI(activityToRemove);

    // update course structure if activity blocks downstream content to update prerequisites
    if (activityToRemove.blocks_downstream_content) {
      const courseData = await UserCourseStorageService.setCourseData(activeCourseData.id);
      dispatch(setActiveCourseData(courseData));
      return;
    }

    // Remove activity and adjust indices
    const filteredActivities = activities.filter((activity) => activity.id !== activityId);
    const adjustedActivities = filteredActivities.map((activity) => ({
      ...activity,
      index:
        (activity.index ?? -1) > (activityToRemove?.index ?? -1)
          ? (activity.index ?? -1) - 1
          : (activity.index ?? -1),
    }));

    // Adjust subunit indices that come after the removed activity
    const unitData = getUnitData();
    const adjustedSubunits: Record<SubunitId, SubunitData> = {};

    Object.entries(unitData?.subunits ?? {}).forEach(([id, subunitData]) => {
      const subunitIndex = subunitData.index ?? -1;
      adjustedSubunits[id as SubunitId] = {
        ...subunitData,
        index: subunitIndex > (activityToRemove?.index ?? -1) ? subunitIndex - 1 : subunitIndex,
      };
    });

    const newCourseData: CourseData = {
      ...activeCourseData,
      units: {
        ...activeCourseData.units,
        [activeUnitId as UnitId]: {
          ...unitData,
          subunits: adjustedSubunits,
          activities: adjustedActivities,
        },
      },
    };

    dispatch(setActiveCourseData(newCourseData));
  };

  return (
    <ConfirmationModal
      modalName='DeleteContent'
      title={`Delete "${currentActivity?.name ?? 'Undefined Content'}"`}
      subtitle='This action cannot be undone'
      message='Are you sure you want to delete this content?'
      description={`This will permanently remove the ${currentActivity?.type ?? 'content'} and cannot be recovered. ${currentActivity?.type === 'quiz' ? 'All quiz questions and responses will be deleted.' : ''}`}
      confirmText='Delete Content'
      confirmIcon={<DeleteIcon className='w-5 h-5' />}
      isDestructive={true}
      onConfirm={handleDeleteContent}
    />
  );
}

export default DeleteContentModal;
